import styled from '@emotion/styled';
import { useMapContext } from '../../hooks/MapProvider';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlassMinus } from '@fortawesome/pro-regular-svg-icons';
import { usePlacesAutocomplete } from '../../hooks/usePlacesAutoComplete';
import { useContentDetails } from '../../providers';
import { getPageUrl, useAnalyticsClickEvent } from 'rwl-ui';
import { ClickTargets } from 'rwl-shared/dist/models/analytics';

const ZoomOutButton = styled.div`
    position: absolute;
    top: 4px;
    right: 4px;
    display: inline-flex;
    align-items: center;
    gap: 8px; /* Space between the icon and text */
    background-color: white;
    white-space: nowrap; /* Prevents text wrapping */
    padding: 8px 16px; /* Adjust for better spacing */
    border-radius: 24px; /* Rounded corners to match the oval shape */
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.2); /* Subtle shadow */
    border: 1px solid rgba(0, 0, 0, 0.1); /* Light border */
    font-size: 12px; /* Adjust text size */
    font-weight: 500; /* Slightly bold text */
    cursor: pointer;
    width: fit-content; /* Adjust width to fit content */
    height: calc(
        calc(var(--rwl-spacing-md) * 1.5) + calc(--rwl-spacing-sm * 0.5)
    );
    &:hover {
        box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.3); /* Add hover effect */
    }
`;

const Container = styled.div`
    z-index: 1;
    position: absolute;
    top: 12;
    right: 12;
`;

const MapControls = ({ map }: { map: google.maps.Map | null }) => {
    const controlDivRef = useRef<HTMLDivElement | null>(null);
    const { neighborhood, setNeighborhood } = useMapContext();
    const { clearSelectedPlaceId } = usePlacesAutocomplete();
    const { closeDetails } = useContentDetails();
    const sendClickEvent = useAnalyticsClickEvent();

    useEffect(() => {
        if (!map || !neighborhood) return;

        // Create a div to hold the control
        const controlDiv = document.createElement('div');
        controlDivRef.current = controlDiv;

        // Render the React component into the controlDiv
        ReactDOM.render(
            <Container>
                <ZoomOutButton
                    role="button"
                    className="rwl-zoom-out-button"
                    onClick={handleZoomOut}
                >
                    <FontAwesomeIcon
                        icon={faMagnifyingGlassMinus}
                        size="1x"
                        fontSize={16}
                    />
                    Zoom out
                </ZoomOutButton>
            </Container>,
            controlDiv
        );

        // Add the control to the map
        map.controls[google.maps.ControlPosition.TOP_RIGHT].push(controlDiv);

        // Cleanup function to remove the control on unmount
        return () => {
            ReactDOM.unmountComponentAtNode(controlDiv);
            const index = map.controls[google.maps.ControlPosition.TOP_RIGHT]
                .getArray()
                .indexOf(controlDiv);
            if (index > -1) {
                map.controls[google.maps.ControlPosition.TOP_RIGHT].removeAt(
                    index
                );
            }
        };
    }, [map, neighborhood]);

    // Function to handle zooming out
    const handleZoomOut = () => {
        sendClickEvent({
            url: getPageUrl(),
            target: ClickTargets.ALL_AREAS,
        });
        setNeighborhood(undefined);
        clearSelectedPlaceId();
        closeDetails();
    };

    return null; // This component doesn't render anything directly
};

export default MapControls;
